import React from "react";
import { Drawer } from "vaul";

// BottomSheet — every sheet sizes to its CONTENT, capped so long content still scrolls.
//
// It did not used to. Two coupled problems, both fixed here:
//
// 1. THE HEIGHT WAS A FIXED HEIGHT, APPLIED BY ACCIDENT. The height class was built by
//    interpolating the prop into the class name. Tailwind never sees an assembled string, so the
//    rule was emitted only when that exact class text happened to appear somewhere else in src.
//    The prop therefore worked for some values and was silently ignored for others: 60 and 95 were
//    honoured (their text exists in unrelated files), 55 and 80 were not. Two sheets passing
//    different numbers could end up the same height, and adding an unrelated class elsewhere in
//    the app could resize a sheet nobody touched.
//
// 2. flex-1 ON THE INNER PANEL MADE A SHORT SHEET STRETCH. Wherever the parent did get a fixed
//    height, `flex-1` grew the white panel to fill it, so a sheet with three paragraphs opened as
//    tall as one with thirty. That was the visible blank space under the content.
//
// The two were load-bearing TOGETHER: flex-1 could not simply be dropped, because with a fixed
// parent height the white panel would stop short and expose the grey (bg-zinc-100) beneath it.
// Removing the forced height is what makes removing flex-1 safe.
//
// Now: `height` is honoured as a MAXIMUM, via an inline style so no Tailwind class is involved and
// no value can be silently dropped. The panel is content-height and scrolls within that cap.
// Callers that passed a number still get the tall allowance they asked for; they no longer get a
// sheet padded out to it.
//
// @param height  number|string — max height in vh. Default 60, matching the previous default.
function BottomSheet({ sheet, setSheet, children, height = "60vh" }) {
  // Accepts 55, "55" and the legacy "60vh" default. Anything unparseable falls back to 60.
  const maxVh = parseFloat(height) || 60;
  return (
    <Drawer.Root
      open={sheet}
      onClose={() => setSheet(false)}
      aria-labelledby="drawer-title"
    >
      <Drawer.Portal>
        {/* ⚠️ stopPropagation on BOTH the overlay and the panel, and it is not optional.
            Radix portals this subtree into document.body, but REACT PROPAGATES PORTAL EVENTS
            THROUGH THE REACT TREE, NOT THE DOM TREE. So if a sheet is rendered from a component
            that sits inside a clickable region, every click in the overlay or the sheet body
            bubbles into that region's handler.
            That shipped: the cart's Authenticity Promise line opened its modal correctly (its own
            button stops the click), and then CLOSING the modal navigated to the PDP, because the
            overlay's click reached CartItemCard's row handler. Tapping the sheet's own text would
            have done the same.
            A modal overlay must never let a click reach the page behind it, so this belongs here
            rather than at each call site. */}
        <Drawer.Overlay
          area-hidden="true"
          onClick={(e) => {
            e.stopPropagation();
            setSheet(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
        />
        <Drawer.Content
          role="dialog"
          aria-labelledby="drawer-title"
          aria-describedby="drawer-title"
          aria-modal="true"
          /* Same reason as the overlay above: a tap on the sheet's own content must not reach
             whatever clickable region the sheet was rendered from. */
          onClick={(e) => e.stopPropagation()}
          /* maxHeight inline, NOT a Tailwind class: an interpolated class name is invisible to
             Tailwind's scanner, which is what made the old height prop work only for values that
             happened to appear elsewhere in src. An inline style always applies. */
          style={{ maxHeight: `${maxVh}vh` }}
          className="bg-zinc-100 flex flex-col z-50 rounded-t-[10px] mt-24 fixed bottom-0 left-0 right-0 lg:max-w-[480px] lg:mx-auto"
        >
          <Drawer.Title
            className="DialogTitle"
            aria-describedby="dialog"
          ></Drawer.Title>
          <div
            /* No flex-1: the panel takes its content's height, so a short sheet is short.
               min-h-0 lets it shrink below its content inside the flex column when the parent hits
               its maxHeight, which is what allows overflow-y-auto to scroll instead of the sheet
               overflowing past the cap. */
            className="p-4 bg-white rounded-t-[10px] min-h-0 overflow-y-auto"
            id="drawer-title"
          >
            <div className="mx-auto w-1/6 h-1.5 flex-shrink-0 rounded-full bg-zinc-300 mb-8" />
            <div className="max-w-md mx-auto">{children}</div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

export default BottomSheet;
